/**
 * SMS reminder consent — shared frontend constants (issue #4071).
 *
 * Single source of truth for the consent status vocabulary, the 4-state chip appearance,
 * the verbal capture channel options, and the disclosure script. The disclosure text must
 * stay in sync with the backend App\Constants\SmsConsentDisclosure::TEMPLATE (a future
 * shared read-endpoint can collapse the two; FR-6).
 */

// ContactPoint-smsReminderConsent.status codes.
export const SMS_CONSENT_STATUS = {
    NOT_ASKED: 'not-asked',
    OPTED_IN: 'opted-in',
    DECLINED: 'declined',
    OPTED_OUT_CARRIER: 'opted-out-carrier',
};

// Capture methods (mirrors App\Enum\PatientConsentMethod).
export const SMS_CONSENT_METHOD = {
    VERBAL: 'verbal',
    DIGITAL_FORM: 'digital-form',
    SMS_KEYWORD: 'sms-keyword',
    STAFF_REVOKED: 'staff-revoked',
};

// Human-readable labels for capture methods (sentence case). Used wherever a method code
// would otherwise be shown to users (chip popover, form summary).
export const SMS_CONSENT_METHOD_LABELS = {
    [SMS_CONSENT_METHOD.VERBAL]: 'verbal',
    [SMS_CONSENT_METHOD.DIGITAL_FORM]: 'online form',
    [SMS_CONSENT_METHOD.SMS_KEYWORD]: 'text message',
    [SMS_CONSENT_METHOD.STAFF_REVOKED]: 'staff update',
};

// Recorded actions (mirrors App\Enum\PatientConsentAction).
export const SMS_CONSENT_ACTION = {
    GRANTED: 'granted',
    DECLINED: 'declined',
    REVOKED: 'revoked',
    REINSTATED: 'reinstated',
};

// Verbal capture channels (mirrors App\Enum\PatientConsentChannel; verbal subset).
export const SMS_CONSENT_CHANNELS = [
    { value: 'in_person', label: 'In person' },
    { value: 'phone_call', label: 'Phone call' },
];

/**
 * Per-status chip appearance. Colors follow the design system: opted-in = success,
 * opted-out = error, not-asked / declined = neutral grey.
 */
export const SMS_CONSENT_CHIP_CONFIG = {
    [SMS_CONSENT_STATUS.NOT_ASKED]: {
        label: 'Not asked',
        color: 'grey-lighten-1',
        icon: 'question-circle',
    },
    [SMS_CONSENT_STATUS.OPTED_IN]: {
        label: 'Opted in',
        color: 'success',
        icon: 'check-circle',
    },
    [SMS_CONSENT_STATUS.DECLINED]: {
        label: 'Declined',
        color: 'grey',
        icon: 'minus-circle',
    },
    [SMS_CONSENT_STATUS.OPTED_OUT_CARRIER]: {
        label: 'Opted out',
        color: 'error',
        icon: 'times-circle',
    },
};

// Disclosure script, up to but excluding the closing question. Keep in sync with
// App\Constants\SmsConsentDisclosure::TEMPLATE_BODY.
const SMS_CONSENT_DISCLOSURE_CORE =
    '{clinic_name} may send you text messages including appointment reminders and clinical ' +
    'communications from your care team. Message frequency varies. Message and data rates may ' +
    'apply. Reply STOP at any time to opt out. Reply HELP for options.';

/**
 * The terms/privacy sentence. Carrier-required (#5497) - A2P registration was rejected without
 * it. Mirrors SmsConsentDisclosure::WEBSITE_SENTENCE, including the LEADING space and absence of
 * a trailing one, so it can be removed whole when no website is known: no dangling placeholder,
 * no double space.
 */
const SMS_CONSENT_WEBSITE_SENTENCE = ' You can find our mobile terms and privacy policy online at {clinic_website}.';

// The disclosure body up to (but excluding) the closing question, terms/privacy sentence
// included. Mirrors App\Constants\SmsConsentDisclosure::TEMPLATE_BODY.
export const SMS_CONSENT_DISCLOSURE_BODY = SMS_CONSENT_DISCLOSURE_CORE + SMS_CONSENT_WEBSITE_SENTENCE;

/**
 * The closing question staff put to the patient. Split out of the template so the security notice
 * can be composed in before the ask rather than after it (#5328) - a warning that changes the
 * answer has to be heard before the answer is given. Mirrors
 * App\Constants\SmsConsentDisclosure::CONSENT_QUESTION.
 */
export const SMS_CONSENT_CONSENT_QUESTION = 'Do you consent?';

// The full registered script, kept as one constant so it stays byte-comparable against the TCR
// filing. Keep in sync with App\Constants\SmsConsentDisclosure::TEMPLATE.
export const SMS_CONSENT_DISCLOSURE_TEMPLATE = `${SMS_CONSENT_DISCLOSURE_BODY} ${SMS_CONSENT_CONSENT_QUESTION}`;

/**
 * Plain-language warning that SMS is not a secure channel (#5328). Composed in at render time
 * rather than folded into the template above, because that template's bytes are the wording
 * registered with the carrier campaign. Exported separately so the digital widget can render it
 * as its own line beside the checkbox disclosure.
 *
 * Must stay byte-identical to App\Constants\SmsConsentDisclosure::SECURITY_NOTICE - the backend
 * stamps that wording as the audited disclosure snapshot, so a drift here would mean the record
 * claims an acknowledgment of text the patient never saw.
 */
export const SMS_CONSENT_SECURITY_NOTICE = 'Text messages are not secure. Anyone with access to your phone may be able to read them.';

/**
 * The disclosure script split into the three parts a surface lays out, in reading order. Exposed
 * so the verbal form can give the security notice its own line without losing the ordering - the
 * notice belongs between the script body and the closing question, never after it.
 *
 * A null/empty website omits the terms/privacy sentence entirely (#5497).
 */
export function renderSmsConsentDisclosureParts(clinicName, clinicWebsite) {
    const body = clinicWebsite ? SMS_CONSENT_DISCLOSURE_BODY : SMS_CONSENT_DISCLOSURE_BODY.replace(SMS_CONSENT_WEBSITE_SENTENCE, '');

    return {
        body: body.replace('{clinic_name}', clinicName || 'This clinic').replace('{clinic_website}', clinicWebsite || ''),
        notice: SMS_CONSENT_SECURITY_NOTICE,
        question: SMS_CONSENT_CONSENT_QUESTION,
    };
}

/**
 * Render the verbal disclosure. Must produce byte-identical output to the backend
 * SmsConsentDisclosure::render() for the same inputs.
 */
export function renderSmsConsentDisclosure(clinicName, clinicWebsite) {
    const { body, notice, question } = renderSmsConsentDisclosureParts(clinicName, clinicWebsite);
    return `${body} ${notice} ${question}`;
}

/**
 * Self-service disclosure shown above the digital consent widget's checkboxes (#4585 FR-6, #5235).
 * Distinct from the verbal script above — this wording is written for a patient filling out a form,
 * and carries every element TCR requires of an opt-in path (sender identity, message types,
 * frequency, rates, STOP, HELP, privacy/terms reference) because TwilioService::campaignContent()
 * submits it to TCR as the description of this path.
 *
 * Must stay byte-identical to App\Constants\SmsConsentDisclosure::DIGITAL_FORM_TEXT; the backend
 * stamps that exact wording as the event's disclosure snapshot for digital-form captures.
 */
export const SMS_CONSENT_DIGITAL_DISCLOSURE =
    'I consent to receive text messages from this practice at the number(s) below, including ' +
    'appointment reminders, clinical messages from my care team, and links to complete my paperwork. ' +
    'Message frequency varies. Message and data rates may apply. Reply STOP at any time to opt out. ' +
    "Reply HELP for help. This practice's SMS privacy policy and SMS terms and conditions apply.";

/**
 * Derive the consent status for a fhirworks ContactPoint. The status lives on the
 * first-class smsReminderConsent field (legacy-fce); absent ⇒ not-asked. A new or changed
 * number therefore reads as not-asked automatically (FR-10).
 */
export function getConsentStatus(contactPoint) {
    return contactPoint?.smsReminderConsent?.status || SMS_CONSENT_STATUS.NOT_ASKED;
}

/**
 * The patient's mobile phone ContactPoint, or null when none is on file. Consent is scoped
 * to the mobile number, so chip placements derive their state from this entry.
 */
export function getMobileContactPoint(resource) {
    const telecom = resource?.telecom || [];
    return telecom.find((cp) => cp?.system === 'phone' && cp?.use === 'mobile') || null;
}
